import moment from 'moment';
import { Campaign, CampaignUpdateDto } from '../models/campaign';
import { Company, Position, Transaction } from '../models/company';
import { Employee } from '../models/employee';
import { AnswersForScoring, Eval, EvalAnswerType, EvalResult, EvalServiceItem } from '../models/evaluation';
import { Team } from '../models/team';
import { Evaluator, User } from '../models/user';
import { getToken } from './auth-config';

export type ErrorResponse = {
    message: string,
};

export type HttpResult<T = {}> = {
    ok?: boolean,
    status?: number,
    data?: T,
    error?: ErrorResponse,
};

export const fetchJson = <T>(uri: string, init?: RequestInit) => {
    if (uri.startsWith('/'))
        uri = uri.substring(1);
    return fetch(`${process.env.REACT_APP_API_URI}/${uri}`, init)
        .then(r => r.json()
            .then((d) => {
                if (r.ok) return { ok: r.ok, status: r.status, data: d } as HttpResult<T>;
                else return { ok: r.ok, status: r.status, error: d } as HttpResult<T>;
            }))
        .catch(e => { console.log(e); return { error: { message: e } } as HttpResult<T> });
}

export const HasAccessToEval = async () => {
    const token = await getToken();
    return fetchJson<{ hasAccess: boolean }>(`api/user/eval`, {
        headers: { 'Authorization': token }
    });
};

export const GetIsAdminAsync = async () => {
    const token = await getToken();
    return fetchJson<{ isAdmin: boolean }>(`api/user/isadmin`, {
        headers: { 'Authorization': token }
    });
};

export const GetCampaignsAsync = async (companyId: number) => {
    const token = await getToken();
    return fetchJson<Campaign[]>(`api/campaigns?companyId=${companyId}`, {
        headers: { 'Authorization': token }
    });
};

export const AddEmployee = async (employee: Employee) => {
    const token = await getToken();
    return fetchJson(`api/employee`, {
        headers: { 'Authorization': token },
        method: 'post',
        body: JSON.stringify(employee)
    });
};

export const UpdateEmployee = async (employee: Employee) => {
    const token = await getToken();
    return fetchJson(`api/employee`, {
        headers: { 'Authorization': token },
        method: 'put',
        body: JSON.stringify(employee)
    });
};

export const GetEmployee = async (employeeId: number) => {
    const token = await getToken();
    return fetchJson<Employee>(`api/employee/${employeeId}`, {
        headers: { 'Authorization': token },
    });
};

export const UploadOptishiftData = async (companyId: number) => {
    const token = await getToken();
    return fetchJson(`api/employee/upload?companyId=${companyId}`, {
        headers: { 'Authorization': token },
        method: 'post',
    });
};

export const UpdateCampaignAsync = async (campaignUpdate: CampaignUpdateDto) => {
    const token = await getToken();
    return fetch(`${process.env.REACT_APP_API_URI}/api/campaign/update`, {
        headers: { 'Authorization': token },
        method: 'post',
        body: JSON.stringify(campaignUpdate)
    });
};

export const GetUsersAsync = async (all: boolean = false) => {
    const token = await getToken();
    return fetchJson<User[]>(`api/users?all=${all}`, {
        headers: { 'Authorization': token }
    });
};

export const GetEvaluatorsAsync = async (all: boolean = false) => {
    const token = await getToken();
    return fetchJson<Evaluator[]>(`api/evaluators?all=${all}`, {
        headers: { 'Authorization': token }
    });
};

export const RegisterUserAsync = async (user: User) => {
    const token = await getToken();
    return fetchJson(`api/user`, {
        headers: { 'Authorization': token },
        method: 'post',
        body: JSON.stringify(user)
    });
};

export const UpdateUserAsync = async (user: User) => {
    const token = await getToken();
    return fetchJson(`api/user`, {
        headers: { 'Authorization': token },
        method: 'put',
        body: JSON.stringify(user)
    });
};

export const DeleteUserAsync = async (email: string) => {
    const token = await getToken();
    return fetchJson(`api/user?email=${email}`, {
        headers: { 'Authorization': token },
        method: 'delete'
    });
};

export const GetCompanyPositionsAsync = async (companyId: number) => {
    const token = await getToken();
    return fetchJson<Position[]>(`api/company/positions?companyId=${companyId}`, {
        headers: { 'Authorization': token }
    });
};

export const DownloadFileAsync = async (filename: string) => {
    const token = await getToken();
    return fetchJson(`api/transaction/download?filename=${encodeURI(filename)}`, {
        headers: { 'Authorization': token },
    });
};

export const GetTransactionsAsync = async (companyId: number) => {
    const token = await getToken();
    return fetchJson<Transaction>(`api/company/transactions?companyId=${companyId}`, {
        headers: { 'Authorization': token }
    });
};

export const AddPositionToCompanyAsync = async (position: Position, companyId: number) => {
    const token = await getToken();
    return fetchJson(`api/company/position?companyId=${companyId}`, {
        headers: { 'Authorization': token },
        method: 'post',
        body: JSON.stringify(position),
    });
};

export const UpdatePositionNameAsync = async (position: Position, companyId: number) => {
    const token = await getToken();
    return fetchJson(`api/company/positions?companyId=${companyId}`, {
        headers: { 'Authorization': token },
        method: 'put',
        body: JSON.stringify([position] as Position[]),
    });
};

export const GetEvaluationsForTeamAsync = async (teamId: number) => {
    const token = await getToken();
    return fetchJson<Eval[]>(`api/evals?teamid=${teamId}`, {
        headers: { 'Authorization': token },
        method: 'get',
    });
};

export const GetEvaluationWithUidAsync = async (uid: string, version: number) => {
    const token = await getToken();
    return fetchJson<Eval>(`api/eval?uid=${uid}&version=${version}`, {
        headers: { 'Authorization': token },
        method: 'get',
    });
};

export const GetAnswersAsync = async () => {
    const token = await getToken();
    return fetchJson<EvalAnswerType[]>(`api/answer`, {
        headers: { 'Authorization': token },
        method: 'get',
    });
};

export const GetAnswersForEvalAsync = async (uid: string, version: number) => {
    const token = await getToken();
    return fetchJson<EvalAnswerType[]>(`api/answer/eval?uid=${uid}&version=${version}`, {
        headers: { 'Authorization': token },
        method: 'get',
    });
};

export const InsertOrUpdateEvalAsync = async (evaluation: Eval, insertNew: boolean) => {
    const token = await getToken();
    return fetchJson<Eval>(`api/eval`, {
        headers: { 'Authorization': token },
        method: insertNew ? 'post' : 'put',
        body: JSON.stringify(evaluation)
    });
};

export const InsertAnswerAsync = async (name: string) => {
    const token = await getToken();
    return fetchJson<EvalAnswerType>(`api/answer`, {
        headers: { 'Authorization': token, 'Content-Type': 'application/json; charset=utf-8' },
        method: 'post',
        body: JSON.stringify({ name: name })
    });
};

export const UpdateAnswerAsync = async (answer: EvalAnswerType) => {
    const token = await getToken();
    return fetchJson<EvalAnswerType>(`api/answer`, {
        headers: { 'Authorization': token, 'Content-Type': 'application/json; charset=utf-8' },
        method: 'put',
        body: JSON.stringify(answer)
    });
};

export const GetCompaniesAsync = async () => {
    const token = await getToken();
    return fetchJson<Company[]>('api/user/companies', {
        headers: { 'Authorization': token }
    });
};

export const GetCompanyEmployeesAsync = async (companyId: number) => {
    const token = await getToken();
    return fetchJson<Employee[]>(`api/employees?companyId=${companyId}`, {
        headers: { 'Authorization': token }
    });
};

export const InsertTeamAsync = async (team: Team) => {
    const token = await getToken();
    return fetchJson<Team>(`api/team`, {
        headers: { 'Authorization': token },
        method: 'post',
        body: JSON.stringify(team),
    });
};

export const InsertEmployeeToTeamAsync = async (teamId: number, employeeId: number) => {
    const token = await getToken();
    return fetchJson<Team>(`api/team.employee?teamid=${teamId}&employeeid=${employeeId}`, {
        headers: { 'Authorization': token },
        method: 'post',
    });
};

export const GetTeamWithIdAsync = async (teamId: number) => {
    const token = await getToken();
    return fetchJson<Team>(`api/team?teamid=${teamId}`, {
        headers: { 'Authorization': token }
    });
};

export const GetCompanyTeamsAsync = async (companyId: number) => {
    const token = await getToken();
    return fetchJson<Team[]>(`api/teams?companyId=${companyId}`, {
        headers: { 'Authorization': token }
    });
};

export const UpdateTeamAsync = async (team: Team) => {
    const token = await getToken();
    return fetchJson<Team>(`api/team`, {
        headers: { 'Authorization': token },
        method: 'put',
        body: JSON.stringify(team),
    });
};

export const SendAnswersForScoring = async (answers: AnswersForScoring) => {
    const token = await getToken();
    return fetchJson<EvalResult>(`api/score`, {
        headers: { 'Authorization': token },
        method: 'post',
        body: JSON.stringify(answers),
    });
};

export const GetResultsForTeamAsync = async (teamId: number) => {
    const token = await getToken();
    return fetchJson<EvalResult[]>(`api/results?teamid=${teamId}`, {
        headers: { 'Authorization': token },
        method: 'get',
    });
};

export const GetResultsForEmployeeAsync = async (employeeId: number) => {
    const token = await getToken();
    return fetchJson<EvalResult[]>(`api/results?employeeid=${employeeId}`, {
        headers: { 'Authorization': token },
        method: 'get',
    });
};

export const GetResultsForEvaluatorAsync = async (evaluatorId: number) => {
    const token = await getToken();
    return fetchJson<EvalResult[]>(`api/results?evaluatorid=${evaluatorId}`, {
        headers: { 'Authorization': token },
        method: 'get',
    });
};

export const GetResultsForCompanyAsync = async (companyId: number) => {
    const token = await getToken();
    return fetchJson<EvalResult[]>(`api/results?companyid=${companyId}`, {
        headers: { 'Authorization': token },
        method: 'get',
    });
};

export const GetResultWithIdAsync = async(resultId: number) => {
    const token = await getToken();
    return fetchJson<EvalResult>(`api/result/${resultId}`, {
        headers: { 'Authorization': token },
        method: 'get',
    });
};

export const DeleteResultWithIdAsync = async (resultId: number) => {
    const token = await getToken();
    return fetch(`${process.env.REACT_APP_API_URI}/api/result/${resultId}`, {
        headers: { 'Authorization': token },
        method: 'delete',
    });
}

export const AddServiceItemAsync = async (serviceItem: EvalServiceItem) => {
    const token = await getToken();
    return fetchJson<EvalServiceItem>(`api/serviceitem`, {
        headers: { 'Authorization': token },
        method: 'post',
        body: JSON.stringify(serviceItem),
    });
};

export const GetServiceItemsForTeamAsync = async (evalUid: string, evalVersion: number) => {
    const token = await getToken();
    return fetchJson<EvalServiceItem[]>(`api/serviceitem?evaluationUid=${evalUid}&evaluationVersion=${evalVersion}`, {
        headers: { 'Authorization': token },
        method: 'get',
    });
};

export const GetAverageScoreForEvalAsync = async (evalUid: string) => {
    const token = await getToken();
    return fetchJson<{ avgScore: number }>(`api/eval/avg?uid=${evalUid}`, {
        headers: { 'Authorization': token },
        method: 'get',
    });
};

export const GetDetailedReportForTeam = async (teamId: number, from: Date, to: Date, sections: boolean, percentage: boolean, showText: boolean) => {
    const token = await getToken();
    return fetch(`${process.env.REACT_APP_API_URI}/api/report/team?teamid=${teamId}&from=${moment(from).format('YYYY-MM-DD')}&to=${moment(to).format('YYYY-MM-DD')}&sections=${sections}&percentage=${percentage}&text=${showText}`, {
        headers: { 'Authorization': token }
    });
}